import { CopyIcon, RefreshCwIcon, SparklesIcon } from "lucide-react";
import { TooltipIconButton } from "./tooltip-icon-button";
import { FC, useEffect, useState, useRef } from "react";
import { TFunction } from "i18next";

/**
 * Props for the AssistantMessageWrapper component
 */
interface AssistantMessageWrapperProps {
  /** The message content to display */
  children: React.ReactNode;
  /** Text to copy when copy button is clicked */
  copyText?: string;
  /** Callback when copy action is performed */
  onCopy?: () => void;
  /** Callback when refresh button is clicked */
  onRefresh?: () => void;
  /** Translation function for internationalization */
  t: TFunction;
}

/**
 * Wrapper component for AI assistant messages.
 * Provides a consistent layout with avatar, content, and action buttons (copy/refresh).
 *
 * @component
 * @example
 * <AssistantMessageWrapper
 *   copyText="AI response text"
 *   onRefresh={handleRefresh}
 *   t={t}
 * >
 *   <p>AI response content</p>
 * </AssistantMessageWrapper>
 */
const AssistantMessageWrapper: FC<AssistantMessageWrapperProps> = ({
  children,
  copyText = "",
  onCopy,
  onRefresh,
  t
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Handles copying message text to clipboard with user feedback
   */
  const handleCopy = async () => {
    try {
      // Use the Clipboard API to copy the message text
      await navigator.clipboard.writeText(copyText ?? "");
      setCopied(true);

      // Clear any existing timeout to prevent memory leaks
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout and store reference for cleanup
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
      onCopy?.();
    } catch {
      // Silently ignore clipboard errors (e.g., permission denied)
    }
  };

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  return (
    <div className="w-full grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-4 min-w-0">
      <div className="ring-border bg-background col-start-1 row-start-1 flex size-8 shrink-0 items-center justify-center rounded-full ring-1">
        <SparklesIcon className="size-4 text-blue-500" />
      </div>
      <div className="text-foreground col-start-2 row-start-1 wrap-break-word leading-7 min-w-0 max-w-full overflow-wrap-anywhere">
        {children}
      </div>
      <div className="col-start-2 row-start-2 mt-3 flex gap-1 text-muted-foreground">
        <TooltipIconButton tooltip={copied ? t("documentchat.chat.copied") : t("documentchat.chat.copy")} onClick={handleCopy}>
          {/* Swap icon with a short "Copied" label for quick feedback */}
          {copied ? <span className="text-xs font-medium">{t("documentchat.chat.copied")}</span> : <CopyIcon />}
        </TooltipIconButton>
        <TooltipIconButton tooltip={t("documentchat.chat.refresh")} onClick={onRefresh}>
          <RefreshCwIcon />
        </TooltipIconButton>
      </div>
    </div>
  );
};

export default AssistantMessageWrapper;
